import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Divider,
  TextField,
  InputAdornment,
  Checkbox,
  Toolbar,
  FormControlLabel
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Archive as ArchiveIcon,
  Search as SearchIcon,
  PictureAsPdf as PdfIcon,
  SelectAll as SelectAllIcon
} from '@mui/icons-material';
import { 
  collection, 
  getDocs, 
  doc,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const ArchiveViewer = ({ archiveYear, open, onClose }) => {
  const { userData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [personnelData, setPersonnelData] = useState({});
  const [vacancesData, setVacancesData] = useState({});
  const [archiveData, setArchiveData] = useState({
    absences: [],
    messages: [],
    permanences: [],
    metadata: null
  });
  const [stats, setStats] = useState({
    absences: 0,
    messages: 0,
    permanences: 0,
    totalPersonnel: 0
  });

  useEffect(() => {
    if (open && archiveYear && userData?.establishmentId) {
      fetchArchiveData();
    }
  }, [open, archiveYear, userData?.establishmentId]);

  const fetchArchiveData = async () => {
    setLoading(true);
    try {
      
      // Récupérer les métadonnées de l'archive
      const archiveMetaRef = doc(db, 'establishments', userData.establishmentId, 'archives', archiveYear);
      const archiveMetaDoc = await getDoc(archiveMetaRef);
      
      let metadata = null;
      if (archiveMetaDoc.exists()) {
        const data = archiveMetaDoc.data();
        metadata = {
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt || Date.now())
        };
      } else {
      }

      // Récupérer les données du personnel depuis la collection users
      
      let personnelSnapshot = { docs: [] };
      let vacancesData = {};
      
      try {
        // Utiliser la collection users comme visible dans Firebase
        const usersRef = collection(db, 'users');
        const usersSnapshot = await getDocs(usersRef);
        
        // Filtrer seulement les utilisateurs de type personnel de cet établissement
        personnelSnapshot = {
          docs: usersSnapshot.docs.filter(doc => {
            const data = doc.data();
            return data.establishmentId === userData.establishmentId;
          })
        };
      
        // Log des utilisateurs trouvés pour debug
        personnelSnapshot.docs.forEach(doc => {
          const data = doc.data();
        });
      } catch (usersError) {
      }
      
      const personnelMap = {};
      personnelSnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Utiliser la structure Firebase visible dans l'image
        let nomComplet = '';
        if (data.firstName && data.lastName) {
          nomComplet = `${data.firstName} ${data.lastName}`;
        } else if (data.prenom && data.nom) {
          nomComplet = `${data.prenom} ${data.nom}`;
        } else if (data.name) {
          nomComplet = data.name;
        } else {
          nomComplet = 'Nom non défini';
        }
        
        // Utiliser le champ service visible dans Firebase avec plus d'options
        let serviceNom = data.service || data.department || data.departmentName || data.grade || 'SERVICE_GENERAL';
        
        personnelMap[doc.id] = {
          nom: nomComplet,
          service: serviceNom
        };
        
      });
      
      
      // Récupérer les données des vacances pour mapper les périodes
      try {
        
        // D'abord essayer de récupérer depuis les métadonnées de l'archive
        if (metadata && metadata.vacancesInfo) {
          vacancesData = metadata.vacancesInfo;
        } else {
          // Fallback: récupérer depuis la collection vacances actuelle
          const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
          const vacancesSnapshot = await getDocs(vacancesRef);
          
          vacancesSnapshot.docs.forEach(doc => {
            const data = doc.data();
            vacancesData[doc.id] = {
              nom: data.nom || 'Période non définie',
              type: data.type || 'PRINTEMPS',
              debut: data.debut,
              fin: data.fin
            };
          });
        }
        
      } catch (vacancesError) {
      }
      setPersonnelData(personnelMap);
      setVacancesData(vacancesData);

      // Récupérer les absences archivées
      let absences = [];
      try {
        const absencesRef = collection(db, 'establishments', userData.establishmentId, 'archives', archiveYear, 'absences');
        const absencesSnapshot = await getDocs(absencesRef);
        absences = absencesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dateDebut: data.dateDebut?.toDate ? data.dateDebut.toDate() : new Date(data.dateDebut),
            dateFin: data.dateFin?.toDate ? data.dateFin.toDate() : new Date(data.dateFin)
          };
        });
      } catch (absencesError) {
      }

      // Récupérer les messages archivés
      let messages = [];
      try {
        const messagesRef = collection(db, 'establishments', userData.establishmentId, 'archives', archiveYear, 'messages');
        const messagesSnapshot = await getDocs(messagesRef);
        messages = messagesSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            dateEnvoi: data.dateEnvoi?.toDate ? data.dateEnvoi.toDate() : new Date(data.dateEnvoi || Date.now())
          };
        });
      } catch (messagesError) {
      }

      // Récupérer les permanences archivées
      let permanences = [];
      try {
        const permanencesRef = collection(db, 'establishments', userData.establishmentId, 'archives', archiveYear, 'permanences');
        const permanencesSnapshot = await getDocs(permanencesRef);
        
        permanences = permanencesSnapshot.docs.map(doc => {
          const data = doc.data();
          
          // Gérer la date avec validation - essayer différents champs de date
          let dateValue = null;
          const dateFields = ['date', 'datePermanence', 'dateDebut', 'createdAt'];
          
          for (const field of dateFields) {
            if (data[field]) {
              if (data[field].toDate) {
                dateValue = data[field].toDate();
                break;
              } else if (data[field] instanceof Date) {
                dateValue = data[field];
                break;
              } else if (typeof data[field] === 'string' || typeof data[field] === 'number') {
                const parsedDate = new Date(data[field]);
                if (!isNaN(parsedDate.getTime())) {
                  dateValue = parsedDate;
                  break;
                }
              }
            }
          }
          
          // Utiliser personnelId ou userId selon la structure
          const personnelId = data.personnelId || data.userId || data.user || '';
          
          return {
            id: doc.id,
            ...data,
            personnelId: personnelId,
            date: dateValue,
            heureDebut: data.heureDebut || data.startTime || '',
            heureFin: data.heureFin || data.endTime || ''
          };
        });
      } catch (permanencesError) {
      }

      setArchiveData({
        absences,
        messages,
        permanences,
        metadata
      });

      setStats({
        absences: absences.length,
        messages: messages.length,
        permanences: permanences.length,
        totalPersonnel: new Set([
          ...absences.map(a => a.personnelId),
          ...permanences.map(p => p.personnelId)
        ]).size
      });

      // Réinitialiser les sélections quand de nouvelles données arrivent
      setSelectedItems([]);


    } catch (error) {
      console.error('❌ [ArchiveViewer] Erreur lors de la récupération des archives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approuvé': return 'success';
      case 'refusé': return 'error';
      case 'en attente': return 'warning';
      default: return 'default';
    }
  };

  const exportArchiveData = () => {
    const dataToExport = {
      year: archiveYear,
      metadata: archiveData.metadata,
      statistics: stats,
      absences: archiveData.absences,
      messages: archiveData.messages,
      exportedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `archive_${archiveYear}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    const currentData = selectedTab === 0 ? filteredAbsences : 
                       selectedTab === 1 ? filteredMessages : 
                       filteredPermanences;
    const selectedData = currentData.filter(item => selectedItems.includes(item.id));
    
    if (selectedData.length === 0) return;

    const htmlContent = `
      <html>
        <head>
          <title>Archive ${archiveYear}</title>
          <style>
            body { font-family: Arial, sans-serif; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h1>Archive ${archiveYear} - ${selectedTab === 0 ? 'Absences' : selectedTab === 1 ? 'Messages' : 'Permanences'}</h1>
          <table>
            <thead>
              <tr>
                ${selectedTab === 0 
                  ? '<th>Personnel</th><th>Service</th><th>Type</th><th>Dates</th><th>Statut</th>'
                  : selectedTab === 1
                  ? '<th>Date</th><th>Expéditeur</th><th>Destinataire</th><th>Sujet</th><th>Type</th>'
                  : '<th>Personnel</th><th>Service</th><th>Date</th><th>Horaires</th><th>Type</th><th>Lieu</th>'
                }
              </tr>
            </thead>
            <tbody>
              ${selectedData.map(item => `
                <tr>
                  ${selectedTab === 0 
                    ? `<td>${personnelData[item.personnelId]?.nom || item.personnelId}</td>
                       <td>${personnelData[item.personnelId]?.service || 'Non défini'}</td>
                       <td>${item.type}</td>
                       <td>${item.dateDebut ? format(item.dateDebut, 'dd/MM/yyyy', { locale: fr }) : ''} - ${item.dateFin ? format(item.dateFin, 'dd/MM/yyyy', { locale: fr }) : ''}</td>
                       <td>${item.statut}</td>`
                    : selectedTab === 1
                    ? `<td>${item.timestamp ? format(item.timestamp, 'dd/MM/yyyy HH:mm', { locale: fr }) : ''}</td>
                       <td>${item.senderName || item.senderId}</td>
                       <td>${item.recipientName || item.recipientId}</td>
                       <td>${item.subject || 'Sans sujet'}</td>
                       <td>${item.type || 'Message'}</td>`
                    : `<td>${personnelData[item.personnelId]?.nom || item.personnelId}</td>
                       <td>${personnelData[item.personnelId]?.service || 'Non défini'}</td>
                       <td>${item.date && !isNaN(item.date.getTime()) ? format(item.date, 'dd/MM/yyyy', { locale: fr }) : 'Date invalide'}</td>
                       <td>${item.heureDebut} - ${item.heureFin}</td>
                       <td>${item.type || 'Permanence'}</td>
                       <td>${item.lieu || 'Non spécifié'}</td>`
                  }
                </tr>
              `).join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    // Créer et télécharger le fichier HTML (qui peut être converti en PDF)
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `archive_${archiveYear}_${selectedTab === 0 ? 'absences' : selectedTab === 1 ? 'messages' : 'permanences'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSelectAll = () => {
    const currentData = selectedTab === 0 ? filteredAbsences : 
                       selectedTab === 1 ? filteredMessages : 
                       filteredPermanences;
    if (selectedItems.length === currentData.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(currentData.map(item => item.id));
    }
  };

  const handleItemSelect = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Filtrer les données selon le terme de recherche
  const filteredAbsences = archiveData.absences.filter(absence => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const personnelNom = personnelData[absence.personnelId]?.nom || absence.personnelId;
    const personnelService = personnelData[absence.personnelId]?.service || '';
    
    return (
      personnelNom.toLowerCase().includes(searchLower) ||
      personnelService.toLowerCase().includes(searchLower) ||
      absence.type?.toLowerCase().includes(searchLower) ||
      absence.motif?.toLowerCase().includes(searchLower) ||
      absence.statut?.toLowerCase().includes(searchLower)
    );
  });

  const filteredMessages = archiveData.messages.filter(message => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    
    return (
      message.titre?.toLowerCase().includes(searchLower) ||
      message.contenu?.toLowerCase().includes(searchLower) ||
      message.expediteur?.toLowerCase().includes(searchLower) ||
      message.destinataire?.toLowerCase().includes(searchLower)
    );
  });

  const filteredPermanences = archiveData.permanences.filter(permanence => {
    
    // Vérifier si la permanence a une date valide
    if (!permanence.date || isNaN(permanence.date.getTime())) {
      return false;
    }
    
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const personnelNom = personnelData[permanence.personnelId]?.nom || permanence.personnelId;
    const personnelService = personnelData[permanence.personnelId]?.service || '';
    
    return (
      personnelNom.toLowerCase().includes(searchLower) ||
      personnelService.toLowerCase().includes(searchLower) ||
      permanence.type?.toLowerCase().includes(searchLower) ||
      permanence.lieu?.toLowerCase().includes(searchLower) ||
      permanence.description?.toLowerCase().includes(searchLower)
    );
  });

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ArchiveIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Archive {archiveYear}
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={exportToPDF} sx={{ mr: 1 }} disabled={selectedItems.length === 0}>
              <PdfIcon />
            </IconButton>
            <IconButton onClick={exportArchiveData} sx={{ mr: 1 }}>
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Métadonnées et statistiques */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Informations de l'archive
                    </Typography>
                    {archiveData.metadata && (
                      <>
                        <Typography variant="body2" color="text.secondary">
                          Archivé le {format(archiveData.metadata.createdAt, 'dd MMMM yyyy à HH:mm', { locale: fr })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Statut: {archiveData.metadata.status || 'Complété'}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Statistiques
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={`${stats.absences} absences`} 
                        color="warning" 
                        size="small" 
                      />
                      <Chip 
                        label={`${stats.messages} messages`} 
                        color="info" 
                        size="small" 
                      />
                      <Chip 
                        label={`${stats.permanences} permanences`} 
                        color="success" 
                        size="small" 
                      />
                      <Chip 
                        label={`${stats.totalPersonnel} personnel concerné`} 
                        color="default" 
                        size="small" 
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* Barre de recherche et contrôles */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedItems.length > 0 && selectedItems.length === (
                      selectedTab === 0 ? filteredAbsences.length : 
                      selectedTab === 1 ? filteredMessages.length : 
                      filteredPermanences.length
                    )}
                    indeterminate={selectedItems.length > 0 && selectedItems.length < (
                      selectedTab === 0 ? filteredAbsences.length : 
                      selectedTab === 1 ? filteredMessages.length : 
                      filteredPermanences.length
                    )}
                    onChange={handleSelectAll}
                  />
                }
                label="Tout sélectionner"
              />
              <Typography variant="body2" color="text.secondary">
                {selectedItems.length} sélectionné(s)
              </Typography>
            </Box>

            {/* Onglets pour les différentes données */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={selectedTab} onChange={handleTabChange}>
                <Tab label={`Absences (${filteredAbsences.length})`} />
                <Tab label={`Messages (${filteredMessages.length})`} />
                <Tab label={`Permanences (${filteredPermanences.length})`} />
              </Tabs>
            </Box>

            {/* Contenu des onglets */}
            <TabPanel value={selectedTab} index={0}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={filteredAbsences.length > 0 && selectedItems.length > 0 && selectedItems.length === filteredAbsences.length}
                              indeterminate={selectedItems.length > 0 && selectedItems.length < filteredAbsences.length}
                              onChange={handleSelectAll}
                              disabled={filteredAbsences.length === 0}
                            />
                          </TableCell>
                          <TableCell>Personnel</TableCell>
                          <TableCell>Service</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Période</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Motif</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredAbsences.map((absence) => (
                          <TableRow key={absence.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedItems.includes(absence.id)}
                                onChange={() => handleItemSelect(absence.id)}
                              />
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const personnel = personnelData[absence.personnelId];
                                const nom = personnel?.nom || absence.personnelId;
                                return nom;
                              })()}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const personnel = personnelData[absence.personnelId];
                                const service = personnel?.service || 'Non défini';
                                return service;
                              })()}
                            </TableCell>
                            <TableCell>{absence.type}</TableCell>
                            <TableCell>
                              {absence.dateDebut && format(absence.dateDebut, 'dd/MM/yyyy', { locale: fr })}
                              {absence.dateFin && ` - ${format(absence.dateFin, 'dd/MM/yyyy', { locale: fr })}`}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={absence.statut} 
                                color={
                                  absence.statut === 'approuvée' ? 'success' : 
                                  absence.statut === 'refusée' ? 'error' : 'default'
                                }
                                size="small"
                              />
                            </TableCell>
                            <TableCell>{absence.motif}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {filteredAbsences.length === 0 && !loading && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Aucune absence archivée pour cette année.
                    </Alert>
                  )}
                </>
              )}
            </TabPanel>

            <TabPanel value={selectedTab} index={1}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={filteredMessages.length > 0 && selectedItems.length > 0 && selectedItems.length === filteredMessages.length}
                              indeterminate={selectedItems.length > 0 && selectedItems.length < filteredMessages.length}
                              onChange={handleSelectAll}
                              disabled={filteredMessages.length === 0}
                            />
                          </TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Expéditeur</TableCell>
                          <TableCell>Destinataire</TableCell>
                          <TableCell>Sujet</TableCell>
                          <TableCell>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredMessages.map((message) => (
                          <TableRow key={message.id}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedItems.includes(message.id)}
                                onChange={() => handleItemSelect(message.id)}
                              />
                            </TableCell>
                            <TableCell>
                              {message.timestamp && format(message.timestamp, 'dd/MM/yyyy HH:mm', { locale: fr })}
                            </TableCell>
                            <TableCell>{message.senderName || message.senderId}</TableCell>
                            <TableCell>{message.recipientName || message.recipientId}</TableCell>
                            <TableCell>{message.subject || 'Sans sujet'}</TableCell>
                            <TableCell>{message.type || 'Message'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {filteredMessages.length === 0 && !loading && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Aucun message archivé pour cette année.
                    </Alert>
                  )}
                </>
              )}
            </TabPanel>

            {/* Onglet Permanences */}
            <TabPanel value={selectedTab} index={2}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {(() => {
                    
                    if (filteredPermanences.length === 0) {
                      return (
                        <Box sx={{ textAlign: 'center', p: 3 }}>
                          <Typography variant="body1" color="text.secondary">
                            Aucune permanence archivée trouvée pour cette année
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Debug: {archiveData.permanences.length} permanences brutes récupérées
                          </Typography>
                        </Box>
                      );
                    }
                    
                    // Regrouper les permanences par service avec structure complète
                    const permanencesByService = filteredPermanences.reduce((acc, permanence) => {
                      // Récupérer les données du personnel depuis les IDs dans permanence.personnel
                      let service = 'SERVICE_GENERAL';
                      let personnelInfo = null;
                      
                      // Vérifier si permanence.personnel existe et contient des IDs
                      if (permanence.personnel && Array.isArray(permanence.personnel) && permanence.personnel.length > 0) {
                        const personnelId = permanence.personnel[0]; // Prendre le premier personnel
                        personnelInfo = personnelData[personnelId];
                        if (personnelInfo && personnelInfo.service) {
                          service = personnelInfo.service;
                        }
                      }
                      // Fallback sur personnelId si personnel array n'existe pas
                      else if (permanence.personnelId) {
                        personnelInfo = personnelData[permanence.personnelId];
                        if (personnelInfo && personnelInfo.service) {
                          service = personnelInfo.service;
                        }
                      }
                      
                      
                      if (!acc[service]) {
                        acc[service] = {
                          permanences: [],
                          periods: new Set(),
                          dates: []
                        };
                      }
                      
                      // Ajouter la période depuis les données de vacances
                      if (permanence.period && vacancesData[permanence.period]) {
                        const vacanceInfo = vacancesData[permanence.period];
                        acc[service].periods.add(`${vacanceInfo.nom} (${vacanceInfo.type})`);
                      } else if (permanence.period) {
                        // Fallback si les données de vacances ne sont pas trouvées
                        acc[service].periods.add(permanence.period);
                      }
                      
                      // Traiter les dates depuis le champ days
                      if (permanence.days && Array.isArray(permanence.days)) {
                        permanence.days.forEach(day => {
                          try {
                            const date = new Date(day);
                            if (!isNaN(date.getTime())) {
                              acc[service].dates.push(date);
                            }
                          } catch (error) {
                            console.warn('Date invalide:', day);
                          }
                        });
                      }
                      // Fallback sur le champ date si days n'existe pas
                      else if (permanence.date && !isNaN(permanence.date.getTime())) {
                        acc[service].dates.push(permanence.date);
                      }
                      
                      // Ajouter la permanence complète
                      acc[service].permanences.push(permanence);
                      
                      return acc;
                    }, {});
                    

                    // Trier les dates pour chaque service et supprimer les doublons
                    Object.keys(permanencesByService).forEach(service => {
                      const serviceData = permanencesByService[service];
                      
                      // Trier les dates
                      serviceData.dates.sort((a, b) => a - b);
                      
                      // Supprimer les doublons de dates
                      serviceData.dates = serviceData.dates.filter((date, index, arr) => 
                        index === 0 || date.getTime() !== arr[index - 1].getTime()
                      );
                      
                      // Convertir les periods Set en Array pour l'affichage
                      serviceData.periodsArray = Array.from(serviceData.periods);
                    });

                    return (
                      <TableContainer component={Paper}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Service</TableCell>
                              <TableCell>Période de vacance</TableCell>
                              <TableCell>Dates de permanences</TableCell>
                              <TableCell>Nombre de dates</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {Object.entries(permanencesByService).map(([service, serviceData]) => (
                              <TableRow key={service}>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {service}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {serviceData.periodsArray.map((period, index) => (
                                      <Chip
                                        key={index}
                                        label={period}
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                      />
                                    ))}
                                    {serviceData.periodsArray.length === 0 && (
                                      <Typography variant="body2" color="text.secondary">
                                        Non définie
                                      </Typography>
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {serviceData.dates.map((date, index) => (
                                      <Chip
                                        key={index}
                                        label={format(date, 'dd/MM/yyyy', { locale: fr })}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                      />
                                    ))}
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={`${serviceData.dates.length} date${serviceData.dates.length > 1 ? 's' : ''}`}
                                    size="small"
                                    color="success"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                  
                  {filteredPermanences.length === 0 && (
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Aucune permanence trouvée pour cette recherche
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </TabPanel>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArchiveViewer;
